import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useEarlyWarningsFilters } from '../../../hooks/useEarlyWarningsFilters';
import { IconCode, TabFilterOption } from '../../../models/EarlyWarnings';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { PMR_ALERT_PROBLEM_LAST_3_YEARS } from '../../../services/sessionFiltersIndicatorDetailHelper';
import { EarlyWarningsFilters } from '../EarlyWarningsFilters/EarlyWarningsFilters';
import styles from './PmrAlertProblemLast3Years.module.scss';
import { Table } from './Table/Table';
import { handleLoading } from '../../utils';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { translateMultipleText } from '../../../services/stringUtil';
import { useTranslation } from 'react-i18next';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import Filter from '../../Portfolio/Filter/Filter';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import ErrorMessageView from '../../Util/ErrorMessage/ErrorMessageView';

const filterConfigComponent: TabFilterOption[] = [
  {
    id: 0,
    projectQuantity: 0,
    projectLabel: { singular: 'project', plural: 'projects' },
    filterIcon: IconCode.GREEN,
    filterLabel: 'no_alert_or_problem_in_the_last_3_years',
    selected: false,
  },
  {
    id: 1,
    projectQuantity: 0,
    projectLabel: { singular: 'project', plural: 'projects' },
    filterIcon: IconCode.YELLOW,
    filterLabel: 'alert_or_problem_in_one_or_the_last_two_years',
    selected: false,
  },
  {
    id: 2,
    projectQuantity: 0,
    projectLabel: { singular: 'project', plural: 'projects' },
    filterIcon: IconCode.RED,
    filterLabel: 'alert_or_problem_in_the_last_three_years',
    selected: false,
  },
];
const defaultSessionFilterObject = {
  filterConfigState: filterConfigComponent,
  changeProjectType: true,
};
export const PmrAlertProblemLast3Years = () => {
  const { currentIndicator } = useSelector<any, any>(state => state);
  const { filterConfigState, handleEarlyWarningsFilter, handleProjectsQuantities } = useEarlyWarningsFilters({
    sessionKey: PMR_ALERT_PROBLEM_LAST_3_YEARS,
    defaultSessionFilterObject,
  });
  const dispatch = useDispatch();
  const { container, earlyAlertsFilterContainer, bodyContainer } = styles;
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const { t } = useTranslation();

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);
  const [hasError, setHasError] = useState(false);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);
    setHasError(false);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      setHasError(true);
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PMR_ALERT_PROBLEM_LAST_3_YEARS')));
    setIsLoading(false);
  };

  useEffect(() => {
    getFilteredSimpleIndicators(
      dispatch,
      currentIndicator.id,
      currentIndicator.indicatorKey,
      setLoadingData,
      projectTypeFilterChange,
      setHasError,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndicator.id, dispatch, filterIndicators.common]);

  useEffect(() => {
    if (!sessionStorage.getItem(PMR_ALERT_PROBLEM_LAST_3_YEARS)) {
      sessionStorage.setItem(PMR_ALERT_PROBLEM_LAST_3_YEARS, JSON.stringify(defaultSessionFilterObject));
    }
  }, []);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0
    ) {
      handleCommonFilterChange();
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  return (
    <>
      <div className={container}>
        {!hasError ? (
          <>
            <div className={earlyAlertsFilterContainer}>
              <EarlyWarningsFilters filterSet={filterConfigState} handleFilter={handleEarlyWarningsFilter} />
            </div>
            <div className={bodyContainer}>
              <Table
                projectsQuantities={handleProjectsQuantities}
                earlyWarningsTab={[
                  filterConfigState[0].selected,
                  filterConfigState[1].selected,
                  filterConfigState[2].selected,
                ]}
                loading={loadingData}
              />
            </div>
          </>
        ) : (
          <div className="error-message-style">
            <ErrorMessageView
              title={t('title_error_message_view')}
              description={t('desc_error_message_tab')}
              reload={handleCommonFilterChange}
              isIndicator={true}
            />
          </div>
        )}
      </div>
    </>
  );
};

const getFilteredSimpleIndicators = (
  d,
  currentIndicatorId,
  currentIndicatorKey,
  setLoading,
  projectTypeFilterChange?,
  setHasError?,
) => {
  try {
    setHasError?.(false);
    return handleLoading(
      () => d(getProjectsSimpleTableIndicator(currentIndicatorId, currentIndicatorKey, projectTypeFilterChange)),
      setLoading,
    );
  } catch (error) {
    console.error(error, 'Error fetching filtered simple indicators');
    setHasError?.(true);
  }
};
